/**
 * Cart drawer add-ons.
 *
 * Lives in an asset rather than inline in the snippet on purpose. The
 * snippet is only rendered in the drawer's non-empty branch, so on a page
 * loaded with an empty cart the definition never parsed — and when adding
 * the first item morphed the markup in, the injected <script> did not
 * execute either. The element stayed un-upgraded and permanently hidden,
 * which is the single most common way to reach this component: land on a
 * product with nothing in the basket and add one.
 *
 * Loaded from header-actions outside that branch, so the registry is
 * populated whatever the cart is doing.
 */
if (!customElements.get('globrite-cart-addons')) {
  customElements.define(
    'globrite-cart-addons',
    class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-addons-list]');
        this.lastKey = `${this.dataset.seed}|${this.dataset.inCart || ''}`;
        this.load();

        // Delegated from the host and bound once. Binding it to the list on
        // every load stacked a duplicate handler each time, so a card added
        // after a few cart changes fired several add requests.
        this.addEventListener('click', (e) => this.onAdd(e));

        // Horizon re-renders the drawer's contents when the cart changes,
        // which can replace this element's children after the first load
        // has already resolved — leaving an empty strip behind. Watching
        // the dialog's open state means the suggestions are refetched at
        // the moment they are about to be seen, whatever happened to the
        // markup in between.
        const dialog = this.closest('dialog');
        if (!dialog) return;

        this.observer = new MutationObserver(() => {
          if (dialog.hasAttribute('open') && !this.querySelector('.gb-addon')) {
            this.load();
          }
        });
        this.observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

        // The observer only helps when the drawer opens. Change a quantity
        // while it is already open and Horizon morphs the markup underneath
        // us — the cards get wiped without the element being re-inserted, so
        // neither connectedCallback nor the `open` attribute fires and the
        // strip silently empties. That was the "sometimes they disappear".
        // cart:update is the one signal every path goes through.
        //
        // cart:update alone is not enough: it fires *before* the morph
        // replaces the markup, so a listener on it looks at cards that are
        // still on screen, decides there is nothing to do, and returns — and
        // a moment later they are gone with nothing left to trigger a
        // reload. Hence the deferred call, and the observer below as the
        // real backstop.
        this.onCartUpdate = () => requestAnimationFrame(() => this.refresh());
        document.addEventListener('cart:update', this.onCartUpdate);

        // Watches for the cards actually being removed, whatever removed
        // them. This is what makes the strip survive a quantity change made
        // while the drawer is open.
        this.contentObserver = new MutationObserver(() => {
          if (this.loading) return;
          if (!this.querySelector('.gb-addon')) this.refresh();
        });
        this.contentObserver.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.contentObserver?.disconnect();
        document.removeEventListener('cart:update', this.onCartUpdate);
      }

      /**
       * Reload when the cards are gone, or when what is already in the cart
       * has changed — otherwise a product added straight from this strip
       * carries on being suggested.
       */
      refresh() {
        if (this.loading) return;
        const key = `${this.dataset.seed}|${this.dataset.inCart || ''}`;
        if (this.querySelector('.gb-addon') && key === this.lastKey) return;
        this.lastKey = key;
        this.load();
      }

      get excluded() {
        return (this.dataset.inCart || '').split(',').filter(Boolean);
      }

      async load() {
        this.list = this.querySelector('[data-addons-list]');
        const seed = this.dataset.seed;
        if (!seed || !this.list) return;

        // Writing the cards mutates our own subtree, which the observer
        // above would otherwise read as another wipe.
        this.loading = true;
        try {
          await this.fetchAndRender(seed);
        } finally {
          this.loading = false;
        }
      }

      async fetchAndRender(seed) {

        // Complementary first — those are merchant-curated pairings and
        // are the better suggestion when they exist. They only exist once
        // someone has set them up in Search & Discovery, so fall back to
        // Shopify's automatic related products rather than showing an
        // empty strip on a store that hasn't done that yet.
        //
        // limit is deliberately higher than the three shown: anything
        // already in the cart is dropped client-side, and asking for
        // exactly three would leave gaps once those are filtered out.
        const excluded = this.excluded;
        let picks = [];

        for (const intent of ['complementary', 'related']) {
          let products = [];
          try {
            const res = await fetch(
              `${this.dataset.url}.json?product_id=${seed}&limit=8&intent=${intent}`
            );
            if (!res.ok) continue;
            ({ products = [] } = await res.json());
          } catch {
            continue;
          }

          picks = products
            .filter((p) => p.available && !excluded.includes(String(p.id)))
            .slice(0, 3);

          if (picks.length) break;
        }

        if (!picks.length) return;

        this.list.innerHTML = picks.map((p) => this.card(p)).join('');
        this.hidden = false;
      }

      card(product) {
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        const image = product.featured_image
          ? `<img src="${product.featured_image}&width=320" alt="" loading="lazy">`
          : '';
        // product.price arrives in the shop's minor units.
        const price = this.money(product.price);

        return `
          <li class="gb-addon">
            <a class="gb-addon__media" href="${product.url}" tabindex="-1" aria-hidden="true">${image}</a>
            <a class="gb-addon__title" href="${product.url}">${this.escape(product.title)}</a>
            <div class="gb-addon__price">${price}</div>
            <button
              class="gb-addon__add"
              type="button"
              data-variant-id="${variant.id}"
              aria-label="Add ${this.escape(product.title)} to cart"
            >Add</button>
          </li>`;
      }

      escape(value) {
        const el = document.createElement('div');
        el.textContent = value;
        return el.innerHTML;
      }

      // Formatted from the cart's own currency rather than by rebuilding
      // shop.money_format here: that format string contains Liquid
      // placeholder braces, which cannot survive being written into a
      // Liquid template as a literal.
      money(cents) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'GBP',
        }).format(cents / 100);
      }

      async onAdd(event) {
        const button = event.target.closest('.gb-addon__add');
        if (!button) return;

        button.disabled = true;
        button.textContent = 'Adding';

        try {
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('add failed');

          // Hand off to the theme's own cart components rather than
          // re-rendering the drawer here. Dispatching cart:update with no
          // `sections` payload is the documented path for "something
          // changed, refetch": Horizon's cart items component falls back
          // to re-rendering its section from the server, which also keeps
          // the header count and this strip in step.
          const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'globrite-cart-addons',
                data: { source: 'globrite-cart-addons', itemCount: cart.item_count },
              },
            })
          );

          button.textContent = 'Added';
        } catch {
          button.disabled = false;
          button.textContent = 'Add';
        }
      }
    }
  );
}
